import { Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import { ParametersContext } from 'components/ParametersGuard';
import ApplicationConfirmationModal from 'components/applications/ApplicationConfirmationModal';
import ApplicationDecisionModal from 'components/applications/ApplicationDecisionModal';
import ApplicationPageLayout from 'components/applications/ApplicationPageLayout';
import ApplicationQuestionsFormSection from 'components/applications/ApplicationQuestionsFormSection';
import Spinner from 'components/shared/Spinner';
import useUnsavedChangesPrompt from 'hooks/useUnsavedChangesPrompt';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import ApplicationsService, { ApplicationSubmitResponseData } from 'services/api/ApplicationsService';
import routes from 'store/configs/Routes';
import ApplicationStatus from 'store/enums/ApplicationStatus';
import ApplicationAnswer, {
  ApplicationAnswerData,
  ApplicationFileAnswer,
  ApplicationUploadedFile,
  SublistAnswerData,
} from 'store/types/ApplicationAnswer';
import { ApplicationContentSectionView, ApplicationContentView } from 'store/types/ApplicationContent';
import ApplicationQuestion from 'store/types/ApplicationQuestion';
import {
  getAnswersData,
  getApplicationStatusAlert,
  getApplicationTitle,
  getFormattedApplicationStatus,
  getFormattedSections,
  getQuestionId,
} from 'util/Application';
import { getUnsavedChangesPromptMessage } from 'util/Form';
import { defaultSnackbarErrorProps } from 'util/Layout';

import QuestionType from 'store/enums/QuestionType';
import commonStyles from 'styles/common.module.scss';
import pageStyles from '../ApplicationPage.module.scss';

interface EditApplicationPageViewProps {
  data: ApplicationContentView;
  isPrivate?: boolean;
  refetch: () => void;
}

const showAlert = (questionId: string) => {
  const alertElement = document.getElementById(`${questionId}-alert`);
  if (alertElement) {
    alertElement.style.display = 'block';
  }
};

const hideAlert = (questionId: string) => {
  const alertElement = document.getElementById(`${questionId}-alert`);
  if (alertElement) {
    alertElement.style.display = 'none';
  }
};

const showValidationAlerts = (applicationData: ApplicationSubmitResponseData) => {
  applicationData.messages.forEach((item) => {
    showAlert(item.questionId);
  });
};

const clearValidationAlerts = () => {
  const elements = document.querySelectorAll(`[data-alert='true']`);
  elements.forEach((element) => {
    if (element instanceof HTMLElement) {
      element.style.display = 'none';
    }
  });
};

const isBlobFile = (x: unknown): x is File => typeof File !== 'undefined' && x instanceof File;

const EditApplicationPageView: React.FunctionComponent<EditApplicationPageViewProps> = ({
  data,
  isPrivate = false,
  refetch,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { handleSubmit, getValues } = useFormContext();
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const {
    claimsAndDisputes: { claimsApplicationTypeId, disputesApplicationTypeId },
  } = useContext(ParametersContext);
  const { removeUnsavedChangesPrompt, prompt } = useUnsavedChangesPrompt(getUnsavedChangesPromptMessage());
  const [decisionModalOpen, setDecisionModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<ApplicationSubmitResponseData | undefined>(undefined);
  const [sections, setSections] = useState<ApplicationContentSectionView[]>([]);
  const questionsList: ApplicationQuestion[] = useMemo(() => {
    let result: ApplicationQuestion[] = [];

    if (data) {
      (data.sections || []).forEach(({ questions = [] }) => {
        result = result.concat(questions);
      });
    }
    return result;
  }, [data]);

  const hasContent: boolean = useMemo(() => !!(data && data.sections && data.sections.length), [data]);

  const handleDecisionModalOpen = useCallback(() => {
    setDecisionModalOpen(true);
  }, []);

  const handleDecisionModalClose = useCallback(() => {
    setDecisionModalOpen(false);
  }, []);

  useEffect(() => {
    if (data) {
      setSections(getFormattedSections(data.sections));
    }
  }, [data]);

  const submitButtonDisabled: boolean = useMemo(() => !hasContent || loading, [hasContent, loading]);

  const scrollToTop = useCallback(() => {
    const sectionElement: HTMLElement | null = document.getElementById('top');

    if (sectionElement) {
      sectionElement.scrollIntoView();
    }
  }, []);

  const uploadFiles = useCallback(
    (request, answersData: ApplicationAnswer[], files: ApplicationFileAnswer, sublistFiles?: ApplicationFileAnswer) => {
      const result: ApplicationAnswerData[] = answersData;
      const uploadPromiseArray: Promise<ApplicationUploadedFile[]>[] = [];
      const questionAnswerIds = new Map<string, string>();

      const getFileIdentity = (value: unknown): string => {
        const existingId = (value as { id?: string })?.id;
        if (existingId) return `id:${existingId}`;

        if (isBlobFile(value)) {
          return `blob:${value.name}:${value.size}:${value.lastModified}`;
        }

        const name = (value as { name?: string })?.name || '';
        const size = String((value as { size?: number })?.size ?? '');
        return `name:${name}:${size}`;
      };

      const hasDuplicateFiles = (fileList?: unknown[]): boolean => {
        if (!fileList?.length) return false;

        const identities = fileList.map(getFileIdentity).filter(Boolean);
        return new Set(identities).size !== identities.length;
      };

      const hasQuestionDuplicates = Object.values(files || {}).some((questionFiles: unknown) =>
        hasDuplicateFiles(questionFiles as unknown[])
      );

      const hasSublistDuplicates = Object.values(sublistFiles || {}).some((sublistAnswer: unknown) =>
        hasDuplicateFiles((sublistAnswer as { files?: unknown[] })?.files)
      );

      if (hasQuestionDuplicates || hasSublistDuplicates) {
        setLoading(false);
        enqueueSnackbar(
          'Duplicate attachment found in the same question. Remove duplicate files before submitting.',
          defaultSnackbarErrorProps
        );
        return;
      }

      result.forEach(({ questionId, answerId }) => {
        if (questionId && answerId) {
          questionAnswerIds.set(questionId, answerId);
        }
      });

      const findSublistAnswerId = (
        fileQuestionId?: string,
        fileSublistQuestionId?: string,
        fileReferenceKey?: string
      ): string | undefined => {
        if (!fileQuestionId || !fileSublistQuestionId || !fileReferenceKey) {
          return undefined;
        }

        const matchingQuestion = result.find(({ questionId }) => questionId === fileQuestionId);

        const sublistAnswers = matchingQuestion?.sublistAnswers as unknown as SublistAnswerData[][];

        if (!sublistAnswers?.length) {
          return undefined;
        }

        for (const answerSet of sublistAnswers) {
          const matched = answerSet.find(
            ({ sublistQuestionId, referenceKey, answerId }) =>
              sublistQuestionId === fileSublistQuestionId && referenceKey === fileReferenceKey && !!answerId
          );

          if (matched?.answerId) {
            return matched.answerId;
          }
        }

        return undefined;
      };

      if (files) {
        Object.entries(files).forEach(([questionId, files]) => {
          const blobFiles = Array.isArray(files) ? files.filter(isBlobFile) : [];

          if (blobFiles.length) {
            uploadPromiseArray.push(
              ApplicationsService.uploadFile(
                applicationId,
                questionId,
                blobFiles,
                undefined,
                undefined,
                questionAnswerIds.get(questionId),
                false
              )
            );
          }
        });
      }

      if (sublistFiles) {
        Object.entries(sublistFiles).forEach(([_, sublistAnswer]) => {
          const referenceKey = sublistAnswer?.referenceKey;
          const questionId = sublistAnswer?.questionId;
          const sublistQuestionId = sublistAnswer?.sublistQuestionId;
          const files = sublistAnswer?.files;
          const blobFiles = files?.filter(isBlobFile) as File[] | undefined;

          if (blobFiles?.length) {
            const answerId = findSublistAnswerId(questionId, sublistQuestionId, referenceKey);

            uploadPromiseArray.push(
              ApplicationsService.uploadFile(
                applicationId,
                questionId,
                blobFiles,
                sublistQuestionId,
                referenceKey,
                answerId,
                true
              )
            );
          }
        });
      }

      if (!uploadPromiseArray.length) {
        request(answersData);
        return;
      }

      Promise.all(uploadPromiseArray)
        .then((data: ApplicationUploadedFile[][]) => {
          data.flat().forEach((item: ApplicationUploadedFile) => {
            const fileQuestionId: string = getQuestionId(item.questionId);
            const answerIndex = result.findIndex(({ questionId }) => questionId === fileQuestionId);
            if (answerIndex >= 0) {
              if (item.referenceKey && item.sublistQuestionId) {
                const sublistAnswerSet: any = result[answerIndex]?.sublistAnswers || [];
                let setIndex = -1;
                let sublistAnswerIndex = -1;
                sublistAnswerSet.forEach((answerSet: any, outerIndex: number) =>
                  answerSet.forEach(({ sublistQuestionId, referenceKey }: SublistAnswerData, innerIndex: number) => {
                    if (sublistQuestionId === item.sublistQuestionId && referenceKey === item.referenceKey) {
                      setIndex = outerIndex;
                      sublistAnswerIndex = innerIndex;
                    }
                  })
                );

                if (setIndex >= 0 && sublistAnswerIndex >= 0) {
                  const existing = sublistAnswerSet[setIndex][sublistAnswerIndex] || [];
                  if (existing) existing.files = [...(existing.files || []), { id: item.fileId }];
                }
              } else result[answerIndex].files = [...(result[answerIndex].files || []), { id: item.fileId }];
            }
          });

          request(result);
        })
        .catch((errorMessage: string) => {
          setLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [applicationId, enqueueSnackbar]
  );

  const validateComplexFields = useCallback(
    (answersData: ApplicationAnswer[]) => {
      const requiredQuestions = questionsList.filter(({ required }) => required);

      const missingAnswers = requiredQuestions.filter((question) => {
        switch (question.type) {
          case QuestionType.Address:
          case QuestionType.SubList: {
            const sublistAnswers =
              answersData.find(({ questionId }) => questionId === question.id)?.sublistAnswers || [];

            const isMissing = sublistAnswers.length === 0;

            if (isMissing) {
              showAlert(question.id);
            } else {
              hideAlert(question.id);
            }

            return isMissing;
          }

          default:
            return false;
        }
      });

      return missingAnswers.length === 0;
    },
    [questionsList]
  );

  const handleFormInvalid = useCallback(() => {
    const formValues = getValues();
    const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);

    validateComplexFields(answersData);

    scrollToTop();
    setShowValidationError(true);
  }, [getValues, questionsList, scrollToTop, validateComplexFields]);

  const handleFormSave = useCallback(() => {
    setLoading(true);
    const formValues = getValues();
    const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);
    const files: ApplicationFileAnswer = formValues['files'];
    const sublistFiles: ApplicationFileAnswer = formValues['sublistFiles'];

    uploadFiles(
      (data: ApplicationAnswerData[]) =>
        ApplicationsService.updateApplicationContent(applicationId, data)
          .then(() => {
            setLoading(false);
            removeUnsavedChangesPrompt();
            enqueueSnackbar('Application successfully saved', { variant: 'success' });
            refetch();
          })
          .catch((error: string) => {
            setLoading(false);
            enqueueSnackbar(error, defaultSnackbarErrorProps);
          }),
      answersData,
      files,
      sublistFiles
    );
  }, [applicationId, enqueueSnackbar, getValues, uploadFiles, questionsList, removeUnsavedChangesPrompt, refetch]);

  const handleFormSubmit = useCallback(
    (formValues: any) => {
      setLoading(true);
      const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);
      const files: ApplicationFileAnswer = formValues['files'];
      const sublistFiles: ApplicationFileAnswer = formValues['sublistFiles'];

      if (!validateComplexFields(answersData)) {
        scrollToTop();
        setShowValidationError(true);
        setLoading(false);
        return;
      }

      clearValidationAlerts();

      uploadFiles(
        (data: ApplicationAnswerData[]) =>
          ApplicationsService.submitApplicationContent(applicationId, data)
            .then((result: ApplicationSubmitResponseData) => {
              const { isOk, messages = [] } = result;
              setLoading(false);
              setSubmitResult(result);
              if (isOk) {
                removeUnsavedChangesPrompt();
                setShowValidationError(false);
                enqueueSnackbar('Application successfully submitted', { variant: 'success' });
              } else if (messages.length) {
                setShowValidationError(true);
                scrollToTop();
                showValidationAlerts(result);
              }
            })
            .catch((error: string) => {
              setLoading(false);
              enqueueSnackbar(error, defaultSnackbarErrorProps);
            }),
        answersData,
        files,
        sublistFiles
      );
    },
    [
      applicationId,
      enqueueSnackbar,
      questionsList,
      removeUnsavedChangesPrompt,
      scrollToTop,
      uploadFiles,
      validateComplexFields,
    ]
  );

  const applicationsUrl = useMemo(
    () =>
      (claimsApplicationTypeId && data.typeId === claimsApplicationTypeId) ||
      (disputesApplicationTypeId && data.typeId === disputesApplicationTypeId)
        ? routes.claimsAndDisputes
        : data.createdById && data.applicantId !== data.createdById
        ? routes.companyApplications
        : routes.applications,
    [claimsApplicationTypeId, data.applicantId, data.createdById, data.typeId, disputesApplicationTypeId]
  );

  const handleCancel = useCallback(() => {
    history.push(applicationsUrl);
  }, [applicationsUrl, history]);

  return (
    <>
      <ApplicationPageLayout
        data={data}
        footerProps={
          isPrivate
            ? {
                onSubmit: handleFormSave,
                submitButtonName: 'Save',
                submitButtonDisabled,
              }
            : {
                onSubmit: handleSubmit(handleFormSubmit, handleFormInvalid),
                submitButtonName: 'Submit',
                submitButtonDisabled,
                children: (
                  <>
                    <Button
                      color={'secondary'}
                      variant={'outlined'}
                      onClick={handleCancel}
                      className={pageStyles.cancelButton}
                      disabled={loading}
                    >
                      {'Go Back'}
                    </Button>
                    {data.decisionDate && (
                      <Button color={'secondary'} variant={'outlined'} onClick={handleDecisionModalOpen}>
                        {'View Decision'}
                      </Button>
                    )}
                    <Button
                      color={'secondary'}
                      variant={'outlined'}
                      onClick={handleFormSave}
                      disabled={!hasContent || loading}
                      className={pageStyles.saveButton}
                    >
                      {'Save'}
                    </Button>
                  </>
                ),
              }
        }
      >
        <Spinner loading={loading}>
          {data && (
            <>
              <div id={'top'} />
              <div className={pageStyles.statusAlert}>{getApplicationStatusAlert(data)}</div>
              <div className={pageStyles.questionsWrapper}>
                {data.title && <h1 className={commonStyles.pageTitle}>{getApplicationTitle(data)}</h1>}
                <Alert severity={'error'} className={commonStyles.alert} hidden={!showValidationError}>
                  All required fields must be completed before the application can be submitted.
                </Alert>
                {hasContent ? (
                  <ApplicationQuestionsFormSection sections={sections} />
                ) : (
                  <Alert severity={'error'} className={commonStyles.alert}>
                    {'No content found'}
                  </Alert>
                )}
              </div>
            </>
          )}
        </Spinner>
      </ApplicationPageLayout>
      {submitResult && (
        <ApplicationConfirmationModal
          open={submitResult.isOk}
          showPaymentLink={getFormattedApplicationStatus(submitResult.status) === ApplicationStatus.PendingPayment}
          applicationsUrl={applicationsUrl}
        />
      )}
      {prompt}
      {data.decisionDate && (
        <ApplicationDecisionModal onClose={handleDecisionModalClose} open={decisionModalOpen} application={data} />
      )}
    </>
  );
};
export default EditApplicationPageView;
